import { BaseTableRow, getHeaderName, TableProps } from '@/types';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid-pro';
import { SyntheticEvent } from 'react';
import MoreVertIcon from '../../resources/images/icons-more-vertical.svg';
import RemoveIcon from '../../resources/images/icons-remove.svg';
import { Table } from './Table';

interface DocumentTableRow extends BaseTableRow {
  documentId?: string;
  documentName?: string;
  fileId?: string;
  name?: string;
}

interface DocumentTableProps<T extends DocumentTableRow = DocumentTableRow> extends TableProps<T> {
  onDocumentSelect?: (e: any, row: T) => void;
}

export class DocumentTable<T extends DocumentTableRow = DocumentTableRow> extends Table<
  T,
  DocumentTableProps<T>
> {
  static columns = {
    name: {
      field: 'name',
      label: 'tableColumns.name',
    },
    actions: {
      field: 'actions',
      label: 'tableColumns.actions',
    },
    delete: {
      field: 'delete',
      label: 'tableColumns.delete',
    },
    icon: {
      field: 'icon',
      label: 'tableColumns.icon',
    },
  };

  static defaultProps = {
    ...Table.defaultProps,
    columns: [{ type: DocumentTable.columns.name }, { type: DocumentTable.columns.actions }],
    noRowsMessage: undefined,
    rowHasAction: undefined,
  };

  constructor(props: DocumentTableProps<T>) {
    super(props);
  }

  getRowId(e: T): string {
    const { getRowId } = this.props;

    if (getRowId) return String(getRowId(e));

    return this.tableId + '_' + e.documentId;
  }

  onRowClick(params: GridRowParams<T>, e: SyntheticEvent) {
    const { onDocumentSelect } = this.props;
    if (onDocumentSelect) onDocumentSelect(e, params.row);
  }

  initColumns() {
    const { columns, sortable, onDocumentAction, onDeleteDocument } = this.props;

    this.columns = [];

    if (!columns) return;

    columns.forEach((c) => {
      if (this.hasColumn(c)) {
        if (c.type === DocumentTable.columns.name)
          this.columns.push({
            field: 'name',
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            flex: 1,
            filterable: false,
            disableReorder: true,
            valueGetter: c.valueGetter ? c.valueGetter : (_value, row) => row.documentName,
            renderCell: c.renderCell ? c.renderCell : (params) => params.row.documentName,
          });
      }

      if (c.type === DocumentTable.columns.actions)
        this.columns.push({
          field: 'actions',
          type: 'actions',
          filterable: false,
          disableReorder: true,
          align: 'right',
          getActions: (params) => [
            <GridActionsCellItem
              icon={<MoreVertIcon />}
              label='Actions'
              onClick={(e) => onDocumentAction?.(e, params.row)}
              key={Math.random().toString(36).substring(7)}
            />,
          ],
        });
      if (c.type === DocumentTable.columns.delete)
        this.columns.push({
          field: 'actions',
          type: 'actions',
          filterable: false,
          disableReorder: true,
          align: 'right',
          getActions: (params) => [
            <GridActionsCellItem
              icon={<RemoveIcon />}
              label='Actions'
              onClick={(e) => onDeleteDocument?.(e, params.row)}
              key={Math.random().toString(36).substring(7)}
            />,
          ],
        });
      if (c.type === DocumentTable.columns.icon)
        this.columns.push({
          field: 'icon',
          type: 'icon',
          filterable: false,
          disableReorder: true,
          align: 'center',
          renderCell: c.renderCell,
          width: 50,
        });
    });
  }
}
